import { CommonModule } from '@angular/common';
import { HtmlEditorComponent } from '../../../../common/html-editor/html-editor.component';
import { EmailPreviewComponent, previewShown, setPreviewShown } from '../../../../common/html-editor/email-preview.component';
import { InsertVariableComponent } from '../../../../common/html-editor/insert-variable.component';
import { variableLabel } from '../../../../common/html-editor/variables';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService } from '../../../../domain/services/breadcrumb/breadcrumb.service';
import { DialogService } from '../../../../domain/services/dialog/dialog.service';
import { NotificationTemplateService } from '../../../../domain/services/admin/notification-template.service';
import { NotificationTemplateModel } from '../../../../domain/models/notification-template.model';

@Component({
  selector: 'app-notification-template-form',
  standalone: true,
  imports: [
    CommonModule,
    HtmlEditorComponent,
    EmailPreviewComponent,
    InsertVariableComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
  ],
  templateUrl: './notification-template-form.component.html',
})
export class NotificationTemplateFormComponent implements OnInit {
  showPreview = previewShown();
  togglePreview(): void { this.showPreview = !this.showPreview; setPreviewShown(this.showPreview); }

  @ViewChild('editor') editor?: HtmlEditorComponent;
  @ViewChild('plain') plain?: ElementRef<HTMLTextAreaElement>;
  saving = false;
  testing = false;
  label = variableLabel;

  form!: FormGroup;
  template: NotificationTemplateModel | null = null;
  code = '';

  constructor(
    private readonly fb: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly breadcrumbService: BreadcrumbService,
    private readonly dialogService: DialogService,
    private readonly templateService: NotificationTemplateService,
    private readonly snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([
      { icon: 'account_balance', url: '/app/admin/notification-templates', label: 'Administrative' },
      { icon: 'mail', url: '/app/admin/notification-templates', label: 'Automatic notifications' },
    ]);

    this.form = this.fb.group({
      subject: ['', Validators.required],
      content: ['', Validators.required],
      isHtml: [true],
    });

    this.code = this.route.snapshot.params['code'];
    if (this.code) {
      this.templateService.getTemplate(this.code).subscribe((template) => {
        this.template = template;
        this.form.patchValue({
          subject: template.subject,
          content: template.content,
          isHtml: template.isHtml,
        });
      });
    }
  }

  get isHtml(): boolean {
    return this.form?.get('isHtml')?.value === true;
  }

  setHtml(v: boolean): void { this.form.get('isHtml')?.setValue(v); }

  /** Tokens the message can't work without (e.g. the account setup link). */
  get requiredTokens(): string[] {
    return (this.template?.tokens ?? []).filter(t => t.includes('LINK'));
  }

  hasToken(t: string): boolean { return (this.form?.value.content ?? '').includes(t); }

  get missingRequired(): boolean { return this.requiredTokens.some(t => !this.hasToken(t)); }

  insertToken(token: string): void {
    if (this.isHtml) { this.editor?.insert(token); return; }
    const el = this.plain?.nativeElement, c = this.form.get('content')!, v: string = c.value ?? '';
    const at = el?.selectionStart ?? v.length;
    c.setValue(v.slice(0, at) + token + v.slice(el?.selectionEnd ?? at));
  }

  sendTest(): void {
    this.testing = true;
    const raw = this.form.value;
    this.templateService.sendTest(this.code, { subject: raw.subject, content: raw.content, isHtml: raw.isHtml }).subscribe({
      next: r => { this.testing = false; this.snackBar.open(`Test email sent to ${r.sentTo}`, 'OK', { duration: 4000 }); },
      error: () => { this.testing = false; this.dialogService.openErrorDialog('Test not sent', 'The test email could not be sent. Try again in a moment.'); },
    });
  }


  onSave(): void {
    if (this.form.invalid || this.missingRequired) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const raw = this.form.value;
    this.templateService
      .updateTemplate(this.code, {
        subject: raw.subject,
        content: raw.content,
        isHtml: raw.isHtml,
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.dialogService.openSuccesDialog('Saved', 'It applies to the next email the system sends.');
          this.router.navigate(['/app/admin/notification-templates']);
        },
        error: () => {
          this.saving = false;
          this.dialogService.openErrorDialog('Error', 'Could not update the template.');
        },
      });
  }

  goBack(): void {
    this.router.navigate(['/app/admin/notification-templates']);
  }
}
